import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  FileText,
  Paperclip,
  Download,
  UserCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { api } from "@/lib/pp/api";
import { fromYMD } from "@/lib/pp/dates";
import { clearToken } from "@/lib/pp/authSession";

import RecordFormDialog from "@/components/pp/RecordFormDialog";
import PatientFormDialog from "@/components/pp/PatientFormDialog";
import InvoicePreviewDialog from "@/components/pp/InvoicePreviewDialog";
import InvoiceUploadDialog from "@/components/pp/InvoiceUploadDialog";

const BG = "#F5F1E8";
const TEXT_DARK = "#1C2B26";
const MUTED = "#4A5D55";

export default function PatientDetail() {
  const { id: pharmacyId, patientId } = useParams();
  const nav = useNavigate();

  const [patient, setPatient] = useState(null);
  const [pharmacy, setPharmacy] = useState(null);
  const [records, setRecords] = useState([]);
  const [showRecord, setShowRecord] = useState(false);
  const [editingRec, setEditingRec] = useState(null);
  const [previewRec, setPreviewRec] = useState(null);
  const [selectedRecId, setSelectedRecId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [editPatient, setEditPatient] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: pharma }, { data: recs }] = await Promise.all([
        api.get(`/patients/${patientId}`),
        api.get(`/pharmacies/${pharmacyId}`),
        api.get(`/patients/${patientId}/records`),
      ]);
      setPatient(p);
      setPharmacy(pharma);
      setRecords(recs.items || []);
    } catch {
      toast.error("Failed to load patient");
    }
  }, [pharmacyId, patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const uploadInvoice = (recordId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    // Stage the file — the InvoiceUploadDialog will collect the PIN and
    // send it as X-Auth-Pin. No more direct API call here.
    setUploadTarget({ recordId, file });
  };

  const deleteRecord = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/records/${confirmDel.id}`);
      toast.success("Record deleted");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const feesPaid = records
    .filter((r) => r.payment_date)
    .reduce((s, r) => s + (Number(r.fee) || 0), 0);
  const feesPending = records
    .filter((r) => !r.payment_date)
    .reduce((s, r) => s + (Number(r.fee) || 0), 0);

  const money = (v) =>
    `$${(Number(v) || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div style={{ minHeight: "100vh", background: BG, overflowX: "hidden" }}>
      {/* Header */}
      <div
        style={{
          background: "#0F3B2E",
          color: "white",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          onClick={() => nav(`/pharmacy/partners/${pharmacyId}`)}
          data-testid="pp-patient-back"
          style={{
            background: "transparent",
            border: "1px solid rgba(255,255,255,.22)",
            color: "white",
            borderRadius: 8,
            padding: "6px 10px",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          <ArrowLeft size={14} /> {pharmacy?.name || "Back"}
        </button>
        <div style={{ marginLeft: "auto" }}>
          <Button
            variant="ghost"
            onClick={() => {
              clearToken();
              nav("/pharmacy/partners", { replace: true });
            }}
            data-testid="pp-patient-logout"
            className="text-white hover:bg-white/10"
          >
            Log out
          </Button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "22px 20px 60px" }}>
        {/* Patient identity card */}
        <div
          style={{
            background: "white",
            border: "1px solid #E5E2DC",
            borderRadius: 14,
            padding: "18px 20px",
            display: "flex",
            gap: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: "#EBF1EC",
              color: "#2D5A4C",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <UserCircle2 size={28} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div
              style={{
                fontFamily: "'Work Sans', system-ui, sans-serif",
                fontSize: 22,
                fontWeight: 600,
                color: TEXT_DARK,
                letterSpacing: "-0.01em",
              }}
              data-testid="pp-patient-name"
            >
              {patient?.patient_name || "—"}
            </div>
            <div
              style={{
                display: "flex",
                gap: 12,
                fontSize: 12,
                color: MUTED,
                marginTop: 4,
                flexWrap: "wrap",
              }}
            >
              <span>
                Client ID:{" "}
                <b
                  style={{ color: TEXT_DARK, fontFamily: "'IBM Plex Mono', monospace" }}
                  data-testid="pp-patient-client-id"
                >
                  {patient?.client_id || "—"}
                </b>
              </span>
              {patient?.registration_sent ? (
                <Badge className="bg-[#8EBCA9]/20 text-[#2D5A4C] hover:bg-[#8EBCA9]/25 border-0">
                  Registered
                  {patient.registration_sent_date && (
                    <span className="ml-1 opacity-80">
                      · {format(fromYMD(patient.registration_sent_date), "MMM d, yyyy")}
                    </span>
                  )}
                </Badge>
              ) : (
                <Badge className="bg-[#F0A47C]/15 text-[#C97757] border-0">
                  Registration pending
                </Badge>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              variant="outline"
              onClick={() => setEditPatient(true)}
              className="h-9"
              data-testid="pp-patient-edit"
            >
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
            {(() => {
              const selectedRec = records.find((r) => r.id === selectedRecId);
              const selectedIsPaid = selectedRec && !!selectedRec.payment_date;
              // Button is only enabled once the user has selected a record AND
              // that record has a payment date. All other states stay greyed.
              const disabled = !selectedRecId || !selectedIsPaid;
              const label = !selectedRecId
                ? "Select a record"
                : !selectedIsPaid
                  ? "Add payment date first"
                  : "Reconciliation PDF";
              const openPdf = async () => {
                if (disabled) return;
                try {
                  // Fetch as blob so iOS never navigates the CRM tab away.
                  const res = await api.get(
                    `/patients/${patientId}/reconciliation-pdf?record_id=${selectedRecId}`,
                    { responseType: "blob" },
                  );
                  const url = URL.createObjectURL(res.data);
                  window.open(url, "_blank", "noopener,noreferrer");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch {
                  /* ignore */
                }
              };
              return (
                <Button
                  onClick={openPdf}
                  disabled={disabled}
                  className="h-9 bg-[#2D5A4C] hover:bg-[#25493E] text-white disabled:opacity-50 disabled:bg-[#4A5D55] disabled:cursor-not-allowed"
                  data-testid="pp-patient-recon"
                >
                  <Download className="h-3.5 w-3.5 mr-1" /> {label}
                </Button>
              );
            })()}
          </div>
        </div>

        {/* Summary strip */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            marginTop: 16,
          }}
        >
          <SummaryCard label="Records" val={records.length} />
          <SummaryCard label="Paid records" val={records.filter((r) => r.payment_date).length} />
          <SummaryCard label="Fees paid" val={money(feesPaid)} tone="good" />
          <SummaryCard label="Fees pending" val={money(feesPending)} tone={feesPending ? "danger" : undefined} />
        </div>

        {/* Records section */}
        <div
          style={{
            marginTop: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <h2
            style={{
              fontFamily: "'Work Sans', system-ui, sans-serif",
              fontSize: 18,
              fontWeight: 600,
              color: TEXT_DARK,
              margin: 0,
            }}
          >
            Invoice records
          </h2>
          <Button
            onClick={() => {
              setEditingRec(null);
              setShowRecord(true);
            }}
            className="bg-[#0F3B2E] hover:bg-[#0A2A20] text-white h-9"
            data-testid="pp-add-record"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add record
          </Button>
        </div>

        <div
          style={{
            background: "white",
            border: "1px solid #E5E2DC",
            borderRadius: 14,
            marginTop: 10,
          }}
        >
          {/* Desktop / tablet — table view */}
          <div className="hidden sm:block overflow-x-auto">
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
              <thead>
                <tr style={{ background: "#F9F8F6" }}>
                  <Th>Medication</Th>
                  <Th>Qty</Th>
                  <Th>Months</Th>
                  <Th>Fee (CAD)</Th>
                  <Th>Payment date</Th>
                  <Th>Invoice PDF</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E2DC]">
                {records.map((r) => {
                  const selected = r.id === selectedRecId;
                  return (
                  <tr
                    key={r.id}
                    data-testid={`pp-record-row-${r.id}`}
                    onClick={() =>
                      setSelectedRecId((cur) => (cur === r.id ? null : r.id))
                    }
                    className={`cursor-pointer transition-colors ${
                      selected
                        ? "bg-[#2D5A4C]/8 outline outline-2 outline-[#2D5A4C]/50 -outline-offset-2"
                        : "hover:bg-[#F9F8F6]"
                    }`}
                    style={{ userSelect: "none" }}
                  >
                    <Td>{r.medication || "—"}</Td>
                    <Td>{r.quantity ?? "—"}</Td>
                    <Td>{r.months || "—"}</Td>
                    <Td>{r.fee != null ? money(r.fee) : "—"}</Td>
                    <Td>
                      {r.payment_date ? (
                        <span className="text-[#2D5A4C] whitespace-nowrap">
                          {format(fromYMD(r.payment_date), "MMM d, yyyy")}
                        </span>
                      ) : (
                        <span className="text-[#C97757] font-medium whitespace-nowrap">Pending</span>
                      )}
                    </Td>
                    <Td>
                      <div
                        className="flex items-center gap-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                      {r.invoice_filename ? (
                        <>
                          <button
                            onClick={() => setPreviewRec(r)}
                            className="inline-flex items-center gap-1 text-xs text-[#2D5A4C] hover:underline whitespace-nowrap"
                            data-testid={`pp-record-preview-${r.id}`}
                          >
                            <FileText className="h-3.5 w-3.5" /> Preview
                          </button>
                          <label
                            className="inline-flex items-center gap-1 text-xs cursor-pointer text-[#4A5D55] hover:text-[#2D5A4C] hover:underline whitespace-nowrap"
                            data-testid={`pp-record-replace-${r.id}`}
                          >
                            <Paperclip className="h-3.5 w-3.5" /> Replace
                            <input
                              type="file"
                              accept="application/pdf"
                              className="hidden"
                              onChange={(e) => uploadInvoice(r.id, e.target.files?.[0])}
                            />
                          </label>
                        </>
                      ) : (
                        <label
                          className="inline-flex items-center gap-1 text-xs cursor-pointer text-[#2D5A4C] hover:underline whitespace-nowrap"
                          data-testid={`pp-record-upload-${r.id}`}
                        >
                          <Paperclip className="h-3.5 w-3.5" /> Attach PDF
                          <input
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => uploadInvoice(r.id, e.target.files?.[0])}
                          />
                        </label>
                      )}
                      </div>
                    </Td>
                    <Td>
                      <div
                        className="flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => {
                            setEditingRec(r);
                            setShowRecord(true);
                          }}
                          className="p-1.5 rounded hover:bg-slate-100 text-[#4A5D55]"
                          data-testid={`pp-record-edit-${r.id}`}
                          aria-label="Edit record"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setConfirmDel(r)}
                          className="p-1.5 rounded hover:bg-red-50 text-[#B84A4A]"
                          data-testid={`pp-record-delete-${r.id}`}
                          aria-label="Delete record"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                  );
                })}
                {!records.length && (
                  <tr>
                    <td colSpan={7} className="px-4 py-3 text-left text-sm text-[#4A5D55]">
                      No records yet. Add the first invoice record for this patient.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile — card layout, no horizontal scroll */}
          <div className="sm:hidden divide-y divide-[#E5E2DC]">
            {records.map((r) => {
              const selected = r.id === selectedRecId;
              return (
                <div
                  key={r.id}
                  data-testid={`pp-record-card-${r.id}`}
                  onClick={() =>
                    setSelectedRecId((cur) => (cur === r.id ? null : r.id))
                  }
                  className={`p-4 space-y-3 cursor-pointer transition-colors ${
                    selected
                      ? "bg-[#2D5A4C]/8 ring-2 ring-inset ring-[#2D5A4C]"
                      : "hover:bg-[#F9F8F6]"
                  }`}
                  style={{
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[15px] font-semibold text-[#1C2B26]">
                      {r.medication || "—"}
                    </div>
                    <div className="text-xs text-[#4A5D55] mt-0.5">
                      Qty {r.quantity ?? "—"} · Months {r.months || "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[15px] font-semibold text-[#1C2B26] whitespace-nowrap">
                      {r.fee != null ? money(r.fee) : "—"}
                    </div>
                    {r.payment_date ? (
                      <div className="text-[11px] text-[#2D5A4C] whitespace-nowrap mt-0.5" data-testid={`pp-record-paid-m-${r.id}`}>
                        Paid · {format(fromYMD(r.payment_date), "MMM d, yyyy")}
                        {(r.authorized_at || r.invoice_authorized_at) && (() => {
                          const invAuth = new Date(r.authorized_at || r.invoice_authorized_at);
                          const days = Math.round(Math.abs(fromYMD(r.payment_date) - invAuth) / 86400000);
                          return (
                            <span className="text-[10px] text-[#4A5D55] block">
                              {days} day{days === 1 ? "" : "s"} to pay
                            </span>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="text-[11px] text-[#C97757] font-medium mt-0.5">
                        Payment pending
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="flex items-center justify-between gap-2 flex-wrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  {r.invoice_filename ? (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setPreviewRec(r)}
                        className="inline-flex items-center gap-1 text-xs text-[#2D5A4C] hover:underline whitespace-nowrap"
                        data-testid={`pp-record-preview-m-${r.id}`}
                      >
                        <FileText className="h-3.5 w-3.5" /> Preview PDF
                      </button>
                      <label
                        className="inline-flex items-center gap-1 text-xs cursor-pointer text-[#4A5D55] whitespace-nowrap"
                        data-testid={`pp-record-replace-m-${r.id}`}
                      >
                        <Paperclip className="h-3.5 w-3.5" /> Replace
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={(e) => uploadInvoice(r.id, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                  ) : (
                    <label
                      className="inline-flex items-center gap-1 text-xs cursor-pointer text-[#2D5A4C] whitespace-nowrap"
                      data-testid={`pp-record-upload-m-${r.id}`}
                    >
                      <Paperclip className="h-3.5 w-3.5" /> Attach PDF
                      <input
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => uploadInvoice(r.id, e.target.files?.[0])}
                      />
                    </label>
                  )}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingRec(r);
                        setShowRecord(true);
                      }}
                      className="p-1.5 rounded hover:bg-slate-100 text-[#4A5D55]"
                      data-testid={`pp-record-edit-m-${r.id}`}
                      aria-label="Edit record"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setConfirmDel(r)}
                      className="p-1.5 rounded hover:bg-red-50 text-[#B84A4A]"
                      data-testid={`pp-record-delete-m-${r.id}`}
                      aria-label="Delete record"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
            {!records.length && (
              <div className="p-6 text-center text-sm text-[#4A5D55]">
                No records yet. Tap “Add record” to create the first invoice record.
              </div>
            )}
          </div>
        </div>
      </div>

      <RecordFormDialog
        open={showRecord}
        onOpenChange={(v) => {
          setShowRecord(v);
          if (!v) setEditingRec(null);
        }}
        editing={editingRec}
        patientId={patientId}
        clientId={patient?.client_id}
        defaultFee={patient?.default_fee ?? null}
        existingRecords={records}
        onSaved={load}
      />

      <InvoiceUploadDialog
        open={!!uploadTarget}
        onOpenChange={(v) => { if (!v) setUploadTarget(null); }}
        target={uploadTarget}
        onUploaded={load}
      />

      <PatientFormDialog
        open={editPatient}
        onOpenChange={setEditPatient}
        editing={patient}
        pharmacyId={pharmacyId}
        onSaved={load}
      />

      <InvoicePreviewDialog
        open={!!previewRec}
        record={previewRec}
        onOpenChange={(o) => !o && setPreviewRec(null)}
      />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              The record and any attached invoice PDF will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteRecord}
              className="bg-[#B84A4A] hover:bg-[#A03D3D]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const SummaryCard = ({ label, val, tone }) => (
  <div
    style={{
      background: "white",
      border: "1px solid #E5E2DC",
      borderRadius: 12,
      padding: "12px 14px",
    }}
  >
    <div style={{ fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: MUTED }}>
      {label}
    </div>
    <div
      style={{
        fontFamily: "'Work Sans', system-ui, sans-serif",
        fontSize: 22,
        fontWeight: 600,
        color: tone === "danger" ? "#B84A4A" : tone === "good" ? "#2D5A4C" : TEXT_DARK,
        marginTop: 2,
      }}
    >
      {val ?? "—"}
    </div>
  </div>
);

const Th = ({ children }) => (
  <th
    style={{
      textAlign: "left",
      padding: "10px 14px",
      fontSize: 10,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: MUTED,
      fontWeight: 600,
    }}
  >
    {children}
  </th>
);
const Td = ({ children }) => (
  <td style={{ padding: "10px 14px", fontSize: 13, color: TEXT_DARK }}>{children}</td>
);
